/**
 * Corridor coordinate utilities.
 *
 * The admin_list_corridor_data RPC returns corridor stops with inline
 * lat/lng. Some stops may have null or out-of-bounds coordinates (e.g.
 * a newly-created draft stop that hasn't been pinned yet). This utility
 * filters to only stops with valid, in-Dominica coordinates so that
 * Google Maps never receives NaN/undefined values.
 */

export type CorridorStop = {
  stop_id: string;
  stop_name: string;
  display_name?: string | null;
  lat: number | null;
  lng: number | null;
  position: number;
  is_off_route: boolean;
  is_draft: boolean;
};

/**
 * Filter corridor stops to only those with valid, finite coordinates
 * within Dominica's bounding box. Use the returned array for all
 * Google Maps rendering (polylines, markers, bounds). Keep the
 * unfiltered list for sidebar/list UI so admins can still see and
 * fix broken stops.
 */
export function validCoords<T extends { lat: number | null; lng: number | null }>(
  stops: T[] | null | undefined,
): (T & { lat: number; lng: number })[] {
  if (!stops) return [];
  return stops.filter(
    (s): s is T & { lat: number; lng: number } =>
      typeof s.lat === 'number' &&
      typeof s.lng === 'number' &&
      Number.isFinite(s.lat) &&
      Number.isFinite(s.lng) &&
      // Dominica bounding box guard — anything outside is bad data.
      s.lat >= 15.1 &&
      s.lat <= 15.7 &&
      s.lng >= -61.5 &&
      s.lng <= -61.2,
  );
}

/**
 * Turn an `admin_upsert_stop` / `admin_publish_stop` error code into something
 * an operator can act on.
 *
 * The stop RPCs return `{ ok: false, error: '<code>' }` and the callers used to
 * toast the raw code. That was tolerable while every code was self-explanatory,
 * but `position_not_on_island` is not: it means the pin was dragged into the
 * sea, which is the mistake that put twelve stop coordinates in the Caribbean
 * in the first place, and the operator needs to be told that in words.
 *
 * Unknown codes fall through unchanged rather than being swallowed behind a
 * generic message, so a new server-side error is still visible.
 */
export function stopRpcErrorMessage(code: string | null | undefined): string {
  switch (code) {
    case 'position_not_on_island':
      return 'That pin is in the sea. Drag it onto the road where the bus actually stops.';
    case 'coords_required':
      return 'This stop has no location yet. Drag it onto the map first.';
    case 'name_required':
      return 'A stop needs a name.';
    case 'invalid_radius':
      return 'Hub radius must be greater than zero metres.';
    case 'forbidden':
      return 'You do not have permission to edit stops.';
    default:
      return code || 'The stop could not be saved.';
  }
}
